// ============================================================================
// BP-04-02: Documents Frontend - DocumentFilters Component
// ============================================================================

import React, { useState } from 'react';
import {
  Search,
  Filter,
  X,
  Calendar,
  User,
  Tag,
  ChevronDown,
  Check,
} from 'lucide-react';
import { cn } from '@/core/lib/utils';
import type { DocumentFilters as DocumentFiltersType, DocumentType } from '../types/documentsTypes';
import { DOCUMENT_TYPE_LABELS, DOCUMENT_TYPE_COLORS, DocumentType as DocType } from '../types/documentsTypes';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface DocumentFiltersProps {
  filters: DocumentFiltersType;
  onFiltersChange: (filters: Partial<DocumentFiltersType>) => void;
  onClearFilters: () => void;
  activeFilterCount: number;
  children?: Array<{ id: string; name: string }>;
  className?: string;
}

// -----------------------------------------------------------------------------
// FilterDropdown Component
// -----------------------------------------------------------------------------

interface FilterDropdownProps<T> {
  label: string;
  icon: React.ReactNode;
  value: T | T[] | undefined;
  options: Array<{ value: T; label: string; color?: string }>;
  onChange: (value: T | undefined) => void;
  multiple?: boolean;
  placeholder?: string;
}

function FilterDropdown<T extends string>({
  label,
  icon,
  value,
  options,
  onChange,
  multiple = false,
  placeholder = 'All',
}: FilterDropdownProps<T>) {
  const [isOpen, setIsOpen] = useState(false);

  const selectedValues = Array.isArray(value) ? value : value ? [value] : [];
  const hasValue = selectedValues.length > 0;

  const getDisplayLabel = () => {
    if (!hasValue) return placeholder;
    if (selectedValues.length === 1) {
      return options.find((o) => o.value === selectedValues[0])?.label || selectedValues[0];
    }
    return `${selectedValues.length} selected`;
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors',
          hasValue
            ? 'border-sage bg-sage/5 text-sage'
            : 'border-warm-gray/20 hover:border-warm-gray/40 text-navy'
        )}
      >
        {icon}
        <span className="hidden sm:inline">{label}:</span>
        <span className="font-medium">{getDisplayLabel()}</span>
        <ChevronDown className={cn('w-4 h-4 transition-transform', isOpen && 'rotate-180')} />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full left-0 mt-1 z-50 min-w-[200px] bg-white rounded-lg shadow-lg border border-warm-gray/10 py-1 animate-in fade-in slide-in-from-top-2 duration-200">
            {/* Clear option */}
            <button
              onClick={() => {
                onChange(undefined);
                setIsOpen(false);
              }}
              className={cn(
                'w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors',
                !hasValue ? 'text-sage bg-sage/5' : 'text-warm-gray hover:bg-cream'
              )}
            >
              {!hasValue && <Check className="w-4 h-4" />}
              <span className={cn(!hasValue ? 'ml-0' : 'ml-6')}>{placeholder}</span>
            </button>

            <div className="my-1 border-t border-warm-gray/10" />

            {/* Options */}
            {options.map((option) => {
              const isSelected = selectedValues.includes(option.value);
              return (
                <button
                  key={option.value}
                  onClick={() => {
                    if (multiple) {
                      // Toggle in multi-select
                      const newValues = isSelected
                        ? selectedValues.filter((v) => v !== option.value)
                        : [...selectedValues, option.value];
                      onChange(newValues.length > 0 ? (newValues as unknown as T) : undefined);
                    } else {
                      onChange(isSelected ? undefined : option.value);
                      setIsOpen(false);
                    }
                  }}
                  className={cn(
                    'w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors',
                    isSelected ? 'text-sage bg-sage/5' : 'text-navy hover:bg-cream'
                  )}
                >
                  {isSelected && <Check className="w-4 h-4" />}
                  <span className={cn(isSelected ? 'ml-0' : 'ml-6')}>{option.label}</span>
                  {option.color && (
                    <span
                      className="w-3 h-3 rounded-full ml-auto"
                      style={{ backgroundColor: option.color }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// DocumentFilters Component
// -----------------------------------------------------------------------------

export const DocumentFilters: React.FC<DocumentFiltersProps> = ({
  filters,
  onFiltersChange,
  onClearFilters,
  activeFilterCount,
  children = [],
  className,
}) => {
  const [searchValue, setSearchValue] = useState(filters.search || '');

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onFiltersChange({ search: searchValue || undefined });
  };

  const documentTypeOptions = Object.entries(DOCUMENT_TYPE_LABELS).map(([value, label]) => ({
    value: value as DocumentType,
    label,
    color: DOCUMENT_TYPE_COLORS[value as DocumentType],
  }));

  const childOptions = children.map((child) => ({
    value: child.id,
    label: child.name,
  }));

  return (
    <div className={cn('space-y-3', className)}>
      {/* Search bar */}
      <form onSubmit={handleSearchSubmit} className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-gray" />
        <input
          type="text"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          placeholder="Search documents..."
          className="w-full pl-10 pr-4 py-2.5 bg-white border border-warm-gray/20 rounded-lg text-navy placeholder:text-warm-gray/60 focus:outline-none focus:border-sage focus:ring-2 focus:ring-sage/10 transition-all"
        />
        {searchValue && (
          <button
            type="button"
            onClick={() => {
              setSearchValue('');
              onFiltersChange({ search: undefined });
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-warm-gray/10 text-warm-gray transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </form>

      {/* Filter row */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Document type filter */}
        <FilterDropdown
          label="Type"
          icon={<Filter className="w-4 h-4" />}
          value={filters.document_type}
          options={documentTypeOptions}
          onChange={(value) => onFiltersChange({ document_type: value })}
          placeholder="All types"
        />

        {/* Child filter (if children exist) */}
        {childOptions.length > 0 && (
          <FilterDropdown
            label="Child"
            icon={<User className="w-4 h-4" />}
            value={filters.child_id}
            options={childOptions}
            onChange={(value) => onFiltersChange({ child_id: value })}
            placeholder="All children"
          />
        )}

        {/* Date range - simplified */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 px-3 py-2 rounded-lg border border-warm-gray/20 text-sm">
            <Calendar className="w-4 h-4 text-warm-gray" />
            <input
              type="date"
              value={filters.date_from || ''}
              onChange={(e) => onFiltersChange({ date_from: e.target.value || undefined })}
              className="bg-transparent border-none text-navy focus:outline-none w-28"
              placeholder="From"
            />
          </div>
          <span className="text-warm-gray">to</span>
          <div className="flex items-center gap-1 px-3 py-2 rounded-lg border border-warm-gray/20 text-sm">
            <input
              type="date"
              value={filters.date_to || ''}
              onChange={(e) => onFiltersChange({ date_to: e.target.value || undefined })}
              className="bg-transparent border-none text-navy focus:outline-none w-28"
              placeholder="To"
            />
          </div>
        </div>

        {/* Shared with attorney filter */}
        <button
          onClick={() =>
            onFiltersChange({
              is_shared_with_attorney:
                filters.is_shared_with_attorney === true ? undefined : true,
            })
          }
          className={cn(
            'flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors',
            filters.is_shared_with_attorney
              ? 'border-sage bg-sage/5 text-sage'
              : 'border-warm-gray/20 hover:border-warm-gray/40 text-navy'
          )}
        >
          {filters.is_shared_with_attorney && <Check className="w-4 h-4" />}
          Shared with Attorney
        </button>

        {/* Clear filters */}
        {activeFilterCount > 0 && (
          <button
            onClick={onClearFilters}
            className="flex items-center gap-1 px-3 py-2 text-sm text-red-500 hover:bg-red-50 rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
            Clear ({activeFilterCount})
          </button>
        )}
      </div>
    </div>
  );
};

export default DocumentFilters;

